import { styled } from '@material-ui/styles';
import React, { CSSProperties, useRef } from 'react';

import { GestureEvent } from '../../../../../Events/withEvent';
import { Message } from '../../../../../Locales/I18nService';
import { ButtonVariant, CustomButton } from '../../../../../Web/Components/Views/CustomButton/CustomButton';
import { useIsMobile } from '../../../../../Web/Context/PlatformContext/PlatformContext';
import { useViewState } from '../../../../../Web/Hooks/ViewState/ViewState';
import { BaseDialog } from '../../../Widgets/BaseDialog';
import { DimensInput } from '../../../Widgets/DimensInput';
import { WallResizeDirection, WallUtils } from '../../../Utils/Wall/WallUtils';
import { useMapContext } from '../../../Context/MapContext';
import { Toast } from '../../../../../Web/Services/ToastService';
import { NunitoSansTitle } from 'Web/Components/Widgets/Typographies';

export type SizeLimit = { min: number; max: number };

export type EditWallCoteDialogProps = {
    value?: string;
    onClose?: VoidFunction;
    onValidate: (notLockedWallDirections: Array<WallResizeDirection>, dimens: number) => void;
};

type EditCoteState = {
    dimens?: number;
    dimensOutOfBounds?: boolean;
    limit?: SizeLimit;
    notLockedWallDirections?: Array<WallResizeDirection>;
    disabled?: boolean;
};
export const EditCoteDialog = ({ value = '', onValidate, onClose }: EditWallCoteDialogProps) => {
    const { state, update } = useViewState<EditCoteState>({ dimens: parseInt(value) });
    const map = useMapContext();

    const onSubmit = (e?: GestureEvent) => {
        e?.preventDefault();
        const { notLockedWallDirections, dimens, limit } = state;
        const dimensOutOfBounds = !(dimens! >= limit?.min! && dimens! <= limit?.max!);
        update({ dimensOutOfBounds });
        if (!dimensOutOfBounds && notLockedWallDirections && dimens) {
            onValidate(notLockedWallDirections, dimens);
        }
    };
    React.useEffect(() => {
        const { lockedWallsList = [], coteWallToEdit, rooms } = map.state;
        const notLockedWallDirections = WallUtils.getBestWallResizeDirections(
            coteWallToEdit!,
            lockedWallsList,
            rooms?.find((x) => x.roomId === coteWallToEdit?.roomId)!.walls!,
            rooms?.filter((x) => x.roomId !== coteWallToEdit?.roomId)!.flatMap((x) => x.walls!)!
        );
        console.log('notLockedWallDirections', notLockedWallDirections);
        if (notLockedWallDirections.length > 0) {
            update({
                notLockedWallDirections: notLockedWallDirections.map((x) => x.direction),
                limit: notLockedWallDirections[0].limit,
            });
        } else {
            update({ disabled: true });
            Toast.error('ERROR_ALL_WALLS_LOCKED');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map.state]);

    React.useEffect(() => {
        if (state.dimensOutOfBounds) {
            const { dimens, limit } = state;
            const dimensOutOfBounds = !(dimens! >= limit?.min! && dimens! <= limit?.max!);
            update({ dimensOutOfBounds });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.dimens]);

    const isMobile = useIsMobile();
    const inputRef = useRef<HTMLInputElement>(null);
    return (
        <BaseDialog
            title={<Message id="EditCoteDialog_Title" />}
            subtitle={<Message id="EditCoteDialog_DimensionPropertyLabel" />}
            onClose={onClose}>
            <Body style={{ color: state.dimensOutOfBounds ? '#FF3B30' : '#000000' }}>
                <Message
                    id="EditCoteDialog_DimensionPropertyDescription"
                    values={{ min: state.limit?.min, max: state.limit?.max }}
                />
            </Body>
            <Footer
                style={{ border: `1px solid ${state.dimensOutOfBounds ? '#FF3B30' : '#13123A'}` }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
                onSubmit={onSubmit}>
                <DimensInput
                    id="EditCoteDialog_DimensInput"
                    inputRef={inputRef}
                    value={state.dimens}
                    onChange={(event) => update({ dimens: parseInt(event.target.value) })}
                    style={DimensStyle}
                    onClick={() => inputRef.current?.focus()}
                    autoFocus={!isMobile}
                    disabled={state.disabled}
                />
                <CustomButton
                    type="submit"
                    onClick={onSubmit}
                    disabled={!state.dimens || state.disabled}
                    variant={ButtonVariant.Blue}
                    title={<Message id="COMMON_VALIDATE" />}
                    styles={{ root: BtnStyle }}
                />
            </Footer>
        </BaseDialog>
    );
};

const Body = styled(NunitoSansTitle)({
    marginTop: 4,
    fontWeight: 400,
    fontSize: 12,
    lineHeight: '16.39px',
});

const Footer = styled('form')({
    marginTop: 17,
    display: 'flex',
    alignItems: 'center',
    border: '1px solid #13123A',
    height: 50,
});
const BtnStyle: CSSProperties = { width: 128, minHeight: 48 };
const DimensStyle: CSSProperties = { margin: 0, background: '#FFFFFF', flex: 1 };
